/**
 * @author Mohit Kukreja <mohit.kukreja@314ecorp.com>
 * @description Utilities for mapping patient data to eligibility form fields
 */
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import { YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP, ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS } from 'app-constants/eligibility';
import patientRelationshipOptions from 'app-constants/patientRelationship.json';
import { getParsedCoverageInfo } from 'utils/patients/insurance';

/**
 * Converts a FHIR relationship code (e.g. '18') to the eligibility form relation value (e.g. 'self').
 * Looks up the human-readable label in patientRelationship.json, then finds a case-insensitive
 * match in ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS. Returns '' for empty/unknown FHIR codes,
 * 'other' when the label doesn't match any of the 5 eligibility options.
 */
export const mapFhirRelationToEligibility = (fhirCode: string): EligibilityRequestForm.IPatientRelationship | '' => {
	if (!fhirCode) {
		return '';
	}
	const label = _.find(patientRelationshipOptions, { value: fhirCode })?.label;
	if (!label) {
		return '';
	}
	const match = _.find(ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS, (opt) => opt.label.toLowerCase() === label.toLowerCase());
	return (match?.value as EligibilityRequestForm.IPatientRelationship) || 'other';
};

/**
 * Map FHIR patient data to eligibility form fields
 * Normalizes dateOfBirth to YEAR_MONTH_DATE_FORMAT for the eligibility form DatePicker
 */
export const mapPatientDataToFormFields = (
	patientData: Patients.IDetailedResponse,
	matchingCoverage: Insurance.ICoverageResponseModel,
): Partial<EligibilityRequestForm.IFormModel> => {
	const parsedCoverageInfo = getParsedCoverageInfo(matchingCoverage);

	const fhirPatient = patientData?.fhirResource;
	const patientName = _.first(fhirPatient?.name) || {};
	const patientAddress = _.first(fhirPatient?.address) || {};
	const identifiers = fhirPatient?.identifier || [];

	// Extract SSN & Medicaid ID
	const ssnIdentifier = _.find(identifiers, (id) => id?.type?.coding?.[0]?.code === 'SY');
	const medicaidIdentifier = _.find(identifiers, (id) => id?.type?.coding?.[0]?.code === 'NQ');

	// Normalize DOB to form's expected format (FHIR can be date or dateTime)
	const rawDob = fhirPatient?.birthDate;
	const dateOfBirth = rawDob && dateParser(rawDob)?.isValid() ? dateParser(rawDob)?.format(YEAR_MONTH_DATE_FORMAT) : rawDob || '';

	return {
		firstName: _.first(patientName?.given) || '',
		lastName: patientName?.family || '',
		dateOfBirth,
		gender: fhirPatient?.gender || null,
		state: patientAddress?.state || '',
		ssn: ssnIdentifier?.value || '',
		medicaidId: medicaidIdentifier?.value || '',
		memberId: parsedCoverageInfo?.subscriberInfo?.memberId || '',
		groupNumber: parsedCoverageInfo.otherMeta.groupNumber || '',
		relation:
			mapFhirRelationToEligibility(parsedCoverageInfo?.subscriberInfo?.relationshipCode) || ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP,
	};
};
